import { z } from 'zod';
import { gameInstanceIndexSchema } from './gameInstance';

/**
 * Canonical Session Binding
 *
 * One logical Playjector session: the display/camera/audio/port resources
 * bound to a single running game instance on a host. The peer-metadata
 * `PlayjectorSession` view is re-derived from this schema for wire
 * compatibility.
 *
 * Contract frozen by docs/feature-plans/game-instance-v2/PHASE_1.md.
 */
export const sessionBindingSchema = z.object({
  sessionId: z.string().min(1), // host-generated, unique per running instance
  gameInstanceIndex: gameInstanceIndexSchema, // CANONICAL numeric identity
  virtualDisplayName: z.string().min(1),
  virtualDisplayGuid: z.string().min(1).optional(),
  virtualDisplaySerial: z.string().min(1).optional(),
  virtualDisplayGdiDeviceName: z.string().min(1).optional(),
  virtualDisplayAdapterLuid: z
    .object({
      lowPart: z.number().int(),
      highPart: z.number().int(),
    })
    .optional(),
  virtualDisplayTargetId: z.number().int().nonnegative().optional(),
  shadowCamName: z.string().min(1).optional(),
  audioDeviceName: z.string().min(1).optional(),
  playjectorPort: z.number().int().positive().optional(),
  loggingPort: z.number().int().positive().optional(),
  displayWidth: z.number().int().positive().optional(),
  displayHeight: z.number().int().positive().optional(),
  refreshRate: z.number().int().positive().optional(),
  launcherInstanceId: z.string().min(1).optional(), // launcher-specific instance handle
});
export type SessionBinding = z.infer<typeof sessionBindingSchema>;
